import React, { useMemo, useRef, useState } from 'react';
import DialogContext, { DialogContextType } from 'context/DialogContext';
import { DialogProviderProps, ShowDialogConfig } from './DialogProvider.props';

export const DialogProvider = ({ children }: DialogProviderProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [wrapperProps, setWrapperProps] = useState<Record<string, unknown>>({});
  const [contentProps, setContentProps] = useState<Record<string, unknown>>({});

  const wrapperComponentRef = useRef<React.JSX.Element | null>(null);
  const contentComponentRef = useRef<React.JSX.Element | null>(null);
  const onCloseCallback = useRef<(() => void) | null>(null);

  const showDialog = ({
    wrapper,
    wrapperProps: dialogWrapperProps,
    content,
    contentProps: dialogContentProps,
    onClose,
    ...restConfig
  }: ShowDialogConfig): void => {
    setOpen(true);
    setConfig(restConfig);

    if (dialogWrapperProps) {
      setWrapperProps(dialogWrapperProps as Record<string, unknown>);
    }

    if (dialogContentProps) {
      setContentProps(dialogContentProps as Record<string, unknown>);
    }

    wrapperComponentRef.current = wrapper || null;
    contentComponentRef.current = content || null;
    onCloseCallback.current = onClose || null;
  };

  const hideDialog = (): void => {
    setOpen(false);
    setWrapperProps({});
    setContentProps({});

    wrapperComponentRef.current = null;
    contentComponentRef.current = null;

    if (onCloseCallback.current) {
      onCloseCallback.current();
    }
  };

  const dialogContextProviderValue = useMemo(
    () => ({
      dialog: wrapperComponentRef.current,
      content: contentComponentRef.current,
      wrapperProps: {
        ...wrapperProps,
        ...config,
        open,
        onClose: hideDialog,
      },
      contentProps,
      showDialog: showDialog as DialogContextType['showDialog'],
    }),
    [config, contentProps, open, wrapperProps]
  );

  return <DialogContext.Provider value={dialogContextProviderValue}>{children}</DialogContext.Provider>;
};
